<template>
  <teleport to="body">
    <transition name="confirm-fade">
      <div
        v-if="showModal"
        class="confirm-overlay"
        v-on:click.self="closeModal"
        v-on:keydown.esc.stop="closeModal"
      >
        <transition name="confirm-scale" appear>
          <div
            v-if="showModal"
            class="confirm-panel"
            role="alertdialog"
            aria-modal="true"
            :aria-labelledby="titleId"
            :aria-describedby="messageId"
          >
            <header class="confirm-head">
              <div class="confirm-head-text">
                <span class="confirm-eyebrow">
                  <font-awesome-icon icon="fa-solid fa-triangle-exclamation" />
                  Destructive action
                </span>
                <h2 :id="titleId">{{ title }}</h2>
              </div>
              <button
                type="button"
                class="confirm-close"
                v-on:click="closeModal"
                :disabled="isDeleting"
                aria-label="Close"
              >
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </header>

            <div class="confirm-body">
              <div class="confirm-icon">
                <font-awesome-icon icon="fa-solid fa-trash" />
              </div>
              <div class="confirm-message" :id="messageId">
                <p class="confirm-question">{{ message }}</p>
                <p v-if="recordLabel" class="confirm-record">
                  <span>{{ recordLabel }}</span>
                </p>
                <p class="confirm-note">This action cannot be undone.</p>
              </div>
            </div>

            <footer class="confirm-foot">
              <button
                type="button"
                class="confirm-btn confirm-btn-secondary"
                v-on:click="closeModal"
                :disabled="isDeleting"
              >
                {{ cancelLabel }}
              </button>
              <button
                ref="confirmButton"
                type="button"
                class="confirm-btn confirm-btn-danger"
                v-on:click="deleteRecord"
                :disabled="isDeleting"
              >
                <span v-if="isDeleting" class="confirm-spinner" aria-hidden="true"></span>
                <span>{{ isDeleting ? 'Deleting...' : confirmLabel }}</span>
              </button>
            </footer>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faTriangleExclamation,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

library.add(faTriangleExclamation, faTrash, faXmark);

let uid = 0;

export default {
  name: "DeleteConfirmationModal",
  props: {
    deleteFunction: {
      type: Function,
      required: true,
    },
    title: {
      type: String,
      default: "Confirm Delete",
    },
    message: {
      type: String,
      default: "Are you sure you want to delete this record?",
    },
    recordLabel: {
      type: String,
      default: "",
    },
    confirmLabel: {
      type: String,
      default: "Delete",
    },
    cancelLabel: {
      type: String,
      default: "Close",
    },
  },
  emits: ["close-delete-confirmation-modal", "refresh-records"],
  data() {
    uid += 1;
    return {
      showModal: true,
      isDeleting: false,
      titleId: `confirm-title-${uid}`,
      messageId: `confirm-message-${uid}`,
      previousBodyOverflow: "",
    };
  },
  mounted() {
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.$nextTick(() => {
      if (this.$refs.confirmButton && typeof this.$refs.confirmButton.focus === "function") {
        this.$refs.confirmButton.focus();
      }
    });
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeUnmount() {
    document.body.style.overflow = this.previousBodyOverflow;
    window.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(event) {
      if (this.isDeleting) return;
      if (event.key === "Escape") {
        event.stopPropagation();
        this.closeModal();
      }
    },
    closeModal() {
      if (this.isDeleting) return;
      this.$emit("close-delete-confirmation-modal");
    },
    async deleteRecord() {
      if (this.isDeleting) return;
      this.isDeleting = true;
      try {
        const result = await this.deleteFunction();
        if (result) {
          this.$emit("refresh-records");
          this.$emit("close-delete-confirmation-modal");
        }
      } catch (error) {
        console.error("Delete confirmation error:", error);
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<style scoped>
.confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.confirm-panel {
  width: 100%;
  max-width: 440px;
  background: var(--ehs-color-surface);
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-lg);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22), 0 4px 12px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  color: var(--ehs-color-ink);
  font-family: var(--ehs-font-sans);
}

.confirm-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 22px 16px;
  border-bottom: 1px solid var(--ehs-color-border);
}

.confirm-head-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.confirm-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: var(--ehs-radius-pill);
  background: var(--ehs-color-danger-soft);
  color: var(--ehs-color-danger);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.confirm-eyebrow .svg-inline--fa {
  font-size: 0.7rem;
}

.confirm-head h2 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--ehs-color-ink);
}

.confirm-close {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--ehs-color-border);
  background: var(--ehs-color-surface);
  border-radius: var(--ehs-radius-sm);
  color: var(--ehs-color-muted);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  flex-shrink: 0;
}

.confirm-close:hover:not(:disabled) {
  background: var(--ehs-color-canvas);
  color: var(--ehs-color-ink);
  border-color: var(--ehs-color-border-strong);
}

.confirm-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.confirm-body {
  display: flex;
  gap: 16px;
  padding: 22px;
  align-items: flex-start;
}

.confirm-icon {
  width: 44px;
  height: 44px;
  min-width: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--ehs-color-danger-soft);
  color: var(--ehs-color-danger);
  font-size: 1.05rem;
}

.confirm-message {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.confirm-message .confirm-question {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--ehs-color-ink);
  line-height: 1.45;
}

.confirm-message .confirm-record {
  margin: 0;
  font-size: 0.85rem;
  color: var(--ehs-color-text);
  line-height: 1.4;
  word-break: break-word;
}

.confirm-message .confirm-record span {
  display: inline-block;
  padding: 3px 8px;
  border-radius: var(--ehs-radius-sm);
  background: var(--ehs-color-canvas);
  border: 1px solid var(--ehs-color-border);
  font-family: var(--ehs-font-mono);
  font-size: 0.8rem;
  color: var(--ehs-color-ink);
}

.confirm-message .confirm-note {
  margin: 4px 0 0;
  font-size: 0.78rem;
  color: var(--ehs-color-muted);
}

.confirm-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px 18px;
  border-top: 1px solid var(--ehs-color-border);
  background: var(--ehs-color-canvas);
}

.confirm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 96px;
  padding: 0.55rem 1rem;
  border-radius: var(--ehs-radius-md);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.1s ease;
}

.confirm-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.confirm-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.confirm-btn-secondary {
  background: var(--ehs-color-surface);
  border: 1px solid var(--ehs-color-border);
  color: var(--ehs-color-ink);
}

.confirm-btn-secondary:hover:not(:disabled) {
  background: var(--ehs-color-canvas-strong);
  border-color: var(--ehs-color-border-strong);
}

.confirm-btn-danger {
  background: var(--ehs-color-danger);
  border: 1px solid var(--ehs-color-danger);
  color: #ffffff;
}

.confirm-btn-danger:hover:not(:disabled) {
  background: #dc2626;
  border-color: #dc2626;
}

.confirm-btn-danger:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.25);
}

.confirm-spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  animation: confirm-spin 0.6s linear infinite;
}

@keyframes confirm-spin {
  to { transform: rotate(360deg); }
}

.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.18s ease;
}

.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}

.confirm-scale-enter-active,
.confirm-scale-leave-active {
  transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s ease;
}

.confirm-scale-enter-from,
.confirm-scale-leave-to {
  transform: scale(0.94) translateY(6px);
  opacity: 0;
}

@media (max-width: 480px) {
  .confirm-panel {
    max-width: 100%;
  }
  .confirm-foot {
    flex-direction: column-reverse;
  }
  .confirm-btn {
    width: 100%;
  }
}
</style>
